import clsxm from "@/lib/clsxm";
import {Link} from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {EditorContent, useEditor, type JSONContent} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {Fragment, Node, Slice, type ResolvedPos} from "prosemirror-model";
import {type FC} from "react";

// A custom clipboard text parser that helps preserve empty lines in the input pasted from the clipboard
// (Based on https://github.com/ueberdosis/tiptap/issues/775)
const clipboardTextParser = (text: string, context: ResolvedPos) => {
  const blocks = text.split(/(?:\r\n?|\n)/);
  const nodes: Node[] = [];

  blocks.forEach(line => {
    const nodeJson: JSONContent = {type: "paragraph"};

    if (line.length > 0) nodeJson.content = [{type: "text", text: line}];

    const node = Node.fromJSON(context.doc.type.schema, nodeJson);
    nodes.push(node);
  });

  const fragment = Fragment.fromArray(nodes);
  return Slice.maxOpen(fragment);
};

const TextareaWithLinks: FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}> = ({value, onChange, placeholder, className, disabled = false}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        bold: false,
        bulletList: false,
        code: false,
        codeBlock: false,
        dropcursor: false,
        hardBreak: false,
        history: false,
        gapcursor: false,
        heading: false,
        horizontalRule: false,
        italic: false,
        listItem: false,
        orderedList: false,
        strike: false,
      }),
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: "https",
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    onCreate: ({editor}) => {
      editor.setOptions({editable: !disabled});
      editor.commands.setContent(value, undefined, {
        preserveWhitespace: "full",
      });
    },
    onUpdate: ({editor}) => {
      onChange(editor.getText({blockSeparator: "\n"}));
    },
    editorProps: {
      clipboardTextParser,
    },
  });

  if (!editor) return null;
  return (
    <EditorContent
      editor={editor}
      className={clsxm(
        "border-primary-light-solid text-neutral-black disabled:bg-neutral-white-dark disabled:text-neutral-grey-dark [&:has(>.ProseMirror-focused)]:border-primary-medium-solid [&_a]:text-primary-dark-solid mt-1 block w-full rounded-md border p-2 shadow-xs disabled:cursor-not-allowed [&_a]:cursor-pointer [&_p]:mb-0 [&>.ProseMirror]:max-h-64 [&>.ProseMirror]:min-h-32 [&>.ProseMirror]:overflow-y-auto [&>.ProseMirror]:outline-hidden",
        className,
        disabled && "bg-neutral-white-dark text-neutral-grey-dark cursor-not-allowed",
      )}
    />
  );
};

export default TextareaWithLinks;
